import React, {CSSProperties} from "react";
import {Box, SxProps, Theme, Typography} from "@mui/material";
import Image from "next/image";
import { Stack } from "@mui/system";
import WistiaVideo, { CircularPlayButton } from "@/components/shared/wistia-video";
import type { VideoData } from "@/types/dto/common/video";
import { Image as ImageData } from "@/types/dto/common/image";
import {deepmerge} from "@mui/utils";

export interface MediaCardSlotProps {
  desktopImageContainer?: {
    sx?: SxProps<Theme>;
  },
  desktopImage?: {
    style?: CSSProperties;
  },
  mobileImageContainer?: {
    sx?: SxProps<Theme>;
  },
  mobileImage?: {
    style?: CSSProperties;
  }
}

interface MediaSectionProps {
  hasVideo: boolean;
  image?: ImageData;
  headerText?: string;
  index: number;
  videoData?: VideoData;
  showImageText?: boolean;
  slotProps?: MediaCardSlotProps;
  useCircularPlayButton?: boolean;
}

const MediaSection: React.FC<MediaSectionProps> = ({
  hasVideo,
  image,
  headerText,
  index,
  videoData,
  showImageText,
  slotProps,
  useCircularPlayButton
}) => (
  <Box
    flexShrink={0}
    sx={{
      width: { lg: "60.1rem", md: "100%", xs: "100%" },
      alignSelf: { lg: !hasVideo ? "stretch" : "flex-start" }
    }}
  >
    {!hasVideo && image?.imageUrl && (
      <Stack
        spacing={1}
        sx={{
          height: { lg: "100%" },
          width: "100%"
        }}
      >
        {/* Mobile and Tablet Image */}
        <Box
          sx={deepmerge({
            display: { xs: "flex", lg: "none" },
            position: "relative",
            width: "100%",
            height: { sm: "40rem", xs: "22.5rem" },
            overflow: "hidden",
            borderRadius: "2rem",
            alignItems: "center",
            justifyContent: "center"
          }, {
            ...slotProps?.mobileImageContainer?.sx
          })}
        >
          <Image
            src={image.imageUrl}
            alt={image.altText || headerText || ""}
            fill
            quality={100}
            loading={index < 2 ? "eager" : "lazy"}
            priority={index === 0}
            style={{
              objectFit: "cover",
              objectPosition: "center",
              userSelect: "none",
              ...slotProps?.mobileImage?.style
            }}
            sizes="(max-width: 1200px) 100vw, 1720px"
            aria-hidden={!image?.altText && !headerText ? "true" : undefined}
            data-testid={`media-text-card-image-mobile-${index}`}
          />
        </Box>
        {/* Desktop image */}
        <Box
          sx={deepmerge({
            display: { xs: "none", lg: "flex" },
            flex: { lg: 1 },
            position: "relative",
            width: "100%",
            maxHeight: "none",
            minHeight: "47.7rem",
            overflow: "hidden",
            borderRadius: "2rem",
            alignItems: "center",
            justifyContent: "center"
          }, {
            ...slotProps?.desktopImageContainer?.sx
          })}
        >
          <Image
            src={image.imageUrl}
            alt={image.altText || headerText || ""}
            fill
            quality={100}
            loading={index < 2 ? "eager" : "lazy"}
            priority={index === 0}
            style={{
              objectFit: "cover",
              objectPosition: "center",
              userSelect: "none",
              ...slotProps?.desktopImage?.style
            }}
            sizes="(max-width: 1200px) 100vw, 1720px"
            aria-hidden={!image?.altText && !headerText ? "true" : undefined}
            data-testid={`media-text-card-image-desktop-${index}`}
          />
        </Box>
        {showImageText && (
          <Typography
            sx={{ fontSize: "1.4rem", fontWeight: 400, paddingLeft: "2rem", fontStyle: "italic" }}
            data-testid={`media-text-card-image-sub-text-${index}`}
          >
             {image?.altText || headerText}
          </Typography>
        )}
      </Stack>
    )}

    {hasVideo && videoData && (
      <Box
        sx={{
          position: "relative",
          height: "auto",
          width: { xs: "100%", tablet: "100%" },
          maxWidth: { xs: "100%", lg: "620px" },
          minWidth: { tablet: "340px" }
        }}
        data-testid={`media-text-card-wistia-video-${index}`}
      >
        <WistiaVideo
          videoId={videoData.wistiaVideoId}
          title={videoData.title}
          startMuted
          deferUntilInteraction
          {...(useCircularPlayButton && { customPlayButton: <CircularPlayButton /> })}
          sx={{ borderRadius: "20px", width: "100%" }}
        />
      </Box>
    )}
  </Box>
);

export default MediaSection;
